/**
 * The shareable path: the caller's own DEDICATED (evergreen) link per kind, in
 * the result shape GeneratedLinksPanel renders. Shared by the send screen's
 * "Get a shareable link" mode and the Submissions header's Shareable links
 * modal, so both produce the identical card from the identical call.
 *
 * Nothing from a form is used — a dedicated link has no recipient and stores
 * `prefill: {}`. It is get-or-create server-side, so this is a fetch as much as
 * a mint and calling it twice yields the same URL.
 *
 * One call PER KIND, unlike sendLinks' single `kinds` array: the dedicated
 * endpoint is keyed on (owner, kind) and takes one. The calls run in parallel
 * because they are independent rows; `axiosPostRequest` resolves only after its
 * own callbacks have run, so each entry is settled by the time its task returns.
 *
 * Resolves with one entry per config, in order — a result object, or `null`
 * where that kind failed (useAxiosHook has already raised its snackbar). The
 * caller decides whether a partial set is acceptable.
 */
export const mintShareableLinks = async ({ createDedicatedLink, configs }) =>
  Promise.all(
    configs.map(async (cfg) => {
      let minted = null;
      await createDedicatedLink({
        kind: cfg.linkKind,
        onSuccessFn: (res) => {
          if (!res?.url) return;
          minted = {
            config: cfg,
            source: "server",
            // Read by GeneratedLinksPanel: an evergreen link is permanent and
            // re-readable, so the panel's copy-it-before-you-leave warning is
            // not merely unnecessary there but wrong.
            linkMode: "evergreen",
            link: res.url,
            linkId: res.link_id,
            expiresAt: null,
            subtitle: "Reusable — anyone who opens it starts a new, blank form",
            droppedFields: [],
            notPrefilled: [],
            emailedTo: null,
            emailError: null,
            recipient: { name: null, email: null },
          };
        },
      });
      return minted;
    }),
  );
